/**
 * Verkleinert und komprimiert Bilder im Browser, bevor sie hochgeladen werden.
 * Friseur-Uploads kommen oft direkt aus der Handy-Kamera (5–12 MB) – das würde
 * die Salon-Seite ausbremsen. Ziel: max. 1800 px Kante, WebP, ~200–400 KB.
 */
const MAX_EDGE = 1800;
const QUALITY = 0.82;

export type CompressedImage = { blob: Blob; extension: string };

export async function compressImage(file: File): Promise<CompressedImage> {
  if (file.type === "image/svg+xml" || typeof document === "undefined") {
    return { blob: file, extension: file.name.split(".").pop() ?? "jpg" };
  }

  try {
    const bitmap = await loadBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas context");
    ctx.drawImage(bitmap, 0, 0, width, height);
    if ("close" in bitmap) bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", QUALITY),
    );
    if (!blob || blob.size >= file.size) {
      return { blob: file, extension: file.name.split(".").pop() ?? "jpg" };
    }
    return { blob, extension: "webp" };
  } catch {
    return { blob: file, extension: file.name.split(".").pop() ?? "jpg" };
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") return createImageBitmap(file);
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}
